import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  D_INV01RP,
  D_INV026,
  D_INV170,
  DISPLAY,
  PROD_PLANO,
  TOKEN,
  USER,
} from "../../constante/localStorage";
import { ResponseTypes } from "../../constante/responseTypes";
import { RequestToSend } from "../../models/RequestToSend";
import { planoAPI } from "./cache";

interface ButtonsState {
  productView: boolean;
  storeView: boolean;
}

interface NotificationsMessage {
  message: string;
  code: ResponseTypes;
  id: number;
}

type appState = {
  appState: string;
  token: string;
  user: string;
  notificationMessages: NotificationsMessage[];
  loadingRequests: string[];
  confirmData: Array<{
    code: string;
    message: string;
    method: string | undefined;
    body: Object;
    url: string;
  }>;
  buttonsState: ButtonsState;
  planoHeaderExpanded: boolean;
  sectionHeaderExpanded: boolean;
  d_inv01rp: any;
  d_inv026: any;
  d_inv170: any;
  prod_plano: any;
  modalsOpened: string[];
  requestsInQueue: RequestToSend[];

  showUCCOnly: boolean;
};

const initialState: appState = {
  appState: "",
  token: localStorage.getItem(TOKEN) ? localStorage.getItem(TOKEN)! : "",
  user: localStorage.getItem(USER) ? localStorage.getItem(USER)! : "",
  notificationMessages: [],
  loadingRequests: [],
  confirmData: [],
  buttonsState: localStorage.getItem(DISPLAY)
    ? JSON.parse(localStorage.getItem(DISPLAY)!)
    : { productView: true, storeView: false },
  planoHeaderExpanded: false,
  sectionHeaderExpanded: false,
  d_inv01rp: localStorage.getItem(D_INV01RP)
    ? JSON.parse(localStorage.getItem(D_INV01RP)!)
    : {},
  d_inv026: localStorage.getItem(D_INV026)
    ? JSON.parse(localStorage.getItem(D_INV026)!)
    : {},
  d_inv170: localStorage.getItem(D_INV170)
    ? JSON.parse(localStorage.getItem(D_INV170)!)
    : {},
  prod_plano: localStorage.getItem(PROD_PLANO)
    ? JSON.parse(localStorage.getItem(PROD_PLANO)!)
    : {},
  modalsOpened: [],
  requestsInQueue: [],
  showUCCOnly: false,
};

export const appStateSlice = createSlice({
  name: "appState",
  initialState,
  reducers: {
    setAppState: (state, action: PayloadAction<string>) => {
      state.appState = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setUser: (state, action: PayloadAction<string>) => {
      state.user = action.payload;
    },
    logout: (state) => {
      localStorage.clear();
      state.token = "";
      planoAPI.util.resetApiState();
    },
    addNotificationMessages: (
      state,
      action: { payload: NotificationsMessage; type: string }
    ) => {
      if (
        state.notificationMessages.filter((n) => n.code === action.payload.code)
          .length === 0
      )
        state.notificationMessages.push(action.payload);
    },
    removeNotificationMessages: (
      state,
      action: { payload: number; type: string }
    ) => {
      state.notificationMessages = state.notificationMessages.filter(
        (n) => n.id !== action.payload
      );
    },
    addLoadingRequest: (state, action) => {
      if (action.payload.url && !action.payload.url.includes("vise")) {
        state.loadingRequests.push(action.payload.url);
      } else if (!action.payload.includes("vise")) {
        state.loadingRequests.push(action.payload);
      }
    },
    removeLoadingRequest: (state, action) => {
      if (action.payload.url && !action.payload.url.includes("vise")) {
        const index = state.loadingRequests.indexOf(action.payload.url);
        state.loadingRequests.splice(index, 1);
      } else if (!action.payload.includes("vise")) {
        const index = state.loadingRequests.indexOf(action.payload);
        state.loadingRequests.splice(index, 1);
      }
    },
    setButtonsState: (state, action: PayloadAction<ButtonsState>) => {
      localStorage.setItem(DISPLAY, JSON.stringify(action.payload));
      state.buttonsState = action.payload;
    },
    setPlanoHeaderExpanded: (state, action: PayloadAction<boolean>) => {
      state.planoHeaderExpanded = action.payload;
    },
    setSectionHeaderExpanded: (state, action: PayloadAction<boolean>) => {
      state.sectionHeaderExpanded = action.payload;
    },
    setd_inv01rp: (state, action) => {
      state.d_inv01rp = action.payload;
      localStorage.setItem(D_INV01RP, JSON.stringify(action.payload));
    },
    setd_inv026: (state, action) => {
      state.d_inv026 = action.payload;
      localStorage.setItem(D_INV026, JSON.stringify(action.payload));
    },
    setd_inv170: (state, action) => {
      state.d_inv170 = action.payload;
      localStorage.setItem(D_INV170, JSON.stringify(action.payload));
    },
    setprod_plano: (state, action) => {
      state.prod_plano = action.payload;
      localStorage.setItem(PROD_PLANO, JSON.stringify(action.payload));
    },
    addModalOpened: (state, action) => {
      const index = state.modalsOpened.indexOf(action.payload);
      if (index === -1) {
        state.modalsOpened.push(action.payload);
      }
    },
    removeModalOpened: (state, action) => {
      const index = state.modalsOpened.indexOf(action.payload);
      if (index > -1) {
        state.modalsOpened.splice(index, 1);
      }
    },
    addRequestToSend: (state, action: PayloadAction<RequestToSend>) => {
      state.requestsInQueue.push(action.payload);
    },
    addRequestsToSend: (state, action: PayloadAction<RequestToSend[]>) => {
      state.requestsInQueue.push(...action.payload);
    },
    removeRequestToSend: (state) => {
      state.requestsInQueue = [];
    },
    setShowUCCOnly: (state, action: PayloadAction<boolean>) => {
      state.showUCCOnly = action.payload;
    },
  },
});

export const {
  setAppState,
  setToken,
  setUser,
  logout,
  addNotificationMessages,
  removeNotificationMessages,
  addLoadingRequest,
  removeLoadingRequest,
  setButtonsState,
  setPlanoHeaderExpanded,
  setSectionHeaderExpanded,
  setd_inv01rp,
  setd_inv026,
  setd_inv170,
  setprod_plano,
  addModalOpened,
  removeModalOpened,
  addRequestToSend,
  addRequestsToSend,
  removeRequestToSend,
  setShowUCCOnly,
} = appStateSlice.actions;

export default appStateSlice.reducer;
